'use client'

import { X } from "lucide-react";
import { colors } from "@/constant/colors";
import NormalButton from "../Button/normal";
import { useMemo } from "react";
import { Quest } from "@/constant/schema";
import { onboardPrepareQuests } from "@/lib/questHelper";
import Image from "@/components/Image";
import useAuthStore from "@/store/useAuthStore";

interface BookshelfUpdateReminderProps {
  visible: boolean;
  onClose: () => void;
  changedFields: {
    grade?: boolean;
    school?: boolean;
  };
  allQuests?: Quest[] | null;
}

const BookshelfUpdateReminder = ({ visible, onClose, changedFields, allQuests }: BookshelfUpdateReminderProps) => {
  const profile = useAuthStore((state) => state.profile);
  const userDistrict = useAuthStore((state) => state.userDistrict);
  // Get recommended quests based on profile
  const recommendedQuests = useMemo(() => {
    if (!allQuests || !visible) return [];
    const filtered = allQuests.filter(q => !q.lock && q.visible);
    const recommended = onboardPrepareQuests(filtered, profile, userDistrict);
    return recommended.slice(0, 5); // Show top 5
  }, [allQuests, visible]);

  if (!visible) return null;

  const changedFieldsText = (() => {
    if (changedFields.grade && changedFields.school) {
      return "grade and school";
    } else if (changedFields.grade) {
      return "grade";
    } else if (changedFields.school) {
      return "school";
    }
    return "profile";
  })();

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-[90vw] max-w-[500px] flex flex-col rounded-2xl p-6 bg-white shadow-xl">
        {/* Header */}
        <div className="flex flex-row justify-between items-start mb-4">
          <h2 className="display-text-bold text-xl flex-1" style={{ color: colors.mainTitleColor }}>
            Your Profile Has Been Updated!
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 h-8 w-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="display-text text-base text-gray-700 mb-4">
            Since you updated your <span className="font-bold">{changedFieldsText}</span>, we've refreshed your book recommendations:
          </p>

          {/* Recommended Books */}
          {recommendedQuests.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex flex-col gap-3">
                {recommendedQuests.map((quest) => (
                  <div key={quest.id} className="flex items-center gap-3 bg-white rounded-md p-2 shadow-sm">
                    <Image
                      src={quest.cover_image || "/assets/default_cover.png"}
                      alt={quest.title}
                      width={40}
                      height={50}
                      className="w-10 h-auto object-cover rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="display-text-bold text-sm text-gray-900 truncate">{quest.title}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="display-text text-sm text-gray-600">
            If you'd like to customize your book selection, you can visit <span className="font-bold">My Bookshelf</span> to manually add or remove books.
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-center">
          <NormalButton
            onClick={onClose}
            className=""
          >
            Got it
          </NormalButton>
        </div>
      </div>
    </div>
  );
};

export default BookshelfUpdateReminder;
